import { parseTrackingCoordinate } from "./tracking-coordinate";

// =============================================================================
// Delivery tracking map state (Issues #79, #80, #82)
//
// Pure helper that turns a `DeliveryTrack` response (or null while loading)
// into the data the tracking page needs to render the map, the visible
// notice copy, the final-state overlay, and the post-tracking CTA.
//
// All business rules live here so the route component and the MapLibre
// wrapper stay free of state-machine and auth knowledge:
//
//   * Marker construction (Issues #79, #80): origin → destination → rider
//     order, with origin snapshot fallback to Organization Delivery origin
//     already enforced by the backend tracking contract.
//   * Rider privacy + freshness (Issue #80): the helper refuses Rider
//     markers when the cached state does not allow them and marks terminal
//     Riders stale regardless of `lastPingAt`.
//   * Notice codes + final-state codes (Issue #82): the helper surfaces
//     one of `loading | preparing | waitingAcceptance | waitingRiderLocation
//     | staleRiderLocation | missingDestination | missingOrderPoints` and,
//     for terminal states, returns a final-state code and a session-aware
//     action code the route renders as an overlay or CTA.
//
// The page (and only the page) translates codes into copy via i18n, then
// hands translated strings down to the map / overlay components.
// =============================================================================

export const RIDER_FRESHNESS_THRESHOLD_MS = 2 * 60 * 1000;

export type DeliveryMarkerKind = "origin" | "destination" | "rider";

export type RiderMarkerFreshness = "live" | "stale";

export type DeliveryMarker = {
  kind: DeliveryMarkerKind;
  lat: number;
  lng: number;
  /** Freshness is only set for `rider` markers. */
  freshness?: RiderMarkerFreshness;
};

export type DeliveryTrackLike = {
  state?: string | null;
  originLat?: string | number | null;
  originLng?: string | number | null;
  destinationLat?: string | number | null;
  destinationLng?: string | number | null;
  lastLat?: string | number | null;
  lastLng?: string | number | null;
  lastPingAt?: string | number | Date | null;
  acceptedAt?: string | number | Date | null;
} | null;

export type SessionState = "loading" | "authenticated" | "guest";

export type DeliveryTrackingNoticeCode =
  | "loading"
  | "preparing"
  | "waitingAcceptance"
  | "waitingRiderLocation"
  | "staleRiderLocation"
  | "missingDestination"
  | "missingOrderPoints";

export type DeliveryFinalStateCode = "delivered" | "cancelled" | "failed";

export type DeliveryTrackingActionCode = { kind: "rateOrder" } | { kind: "backToOrder" };

export type DeliveryTrackingMapState = {
  markers: DeliveryMarker[];
  isEmpty: boolean;
  /** Notice copy to render above the map. Null when no notice applies. */
  notice: DeliveryTrackingNoticeCode | null;
  /** Final-state overlay code. Null when the Delivery is still active. */
  finalState: DeliveryFinalStateCode | null;
  /** Post-tracking CTA. Null when no CTA is appropriate. */
  action: DeliveryTrackingActionCode | null;
};

const RIDER_ACTIVE_STATES = new Set(["accepted", "en_route", "arrived"]);
const RIDER_TERMINAL_STATES = new Set(["delivered", "failed", "cancelled"]);

function isRiderLocationEligible(state: string | null | undefined, acceptedAt: unknown): boolean {
  if (state && RIDER_ACTIVE_STATES.has(state)) {
    return true;
  }
  if (state && RIDER_TERMINAL_STATES.has(state)) {
    if (acceptedAt == null) {
      return false;
    }
    if (acceptedAt instanceof Date) {
      return !Number.isNaN(acceptedAt.getTime());
    }
    return String(acceptedAt).length > 0;
  }
  return false;
}

function parsePingAt(value: unknown): number | null {
  if (value == null) {
    return null;
  }
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value.getTime();
  }
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value === "string") {
    // Accept either an ISO timestamp string or a numeric epoch milliseconds
    // string. `Number("2026-06-25T16:49:30.000Z")` returns NaN, so route
    // string values through `Date.parse` first.
    const asNumeric = Number(value);
    if (Number.isFinite(asNumeric)) {
      return asNumeric;
    }
    const asDate = Date.parse(value);
    return Number.isFinite(asDate) ? asDate : null;
  }
  return null;
}

function classifyRiderFreshness(args: {
  state: string | null | undefined;
  lastPingAt: unknown;
  now: number;
}): RiderMarkerFreshness {
  // Terminal states are always stale even if the last ping is recent;
  // the Delivery is finished and the Rider marker is last-known context.
  if (args.state && RIDER_TERMINAL_STATES.has(args.state)) {
    return "stale";
  }
  const pingAt = parsePingAt(args.lastPingAt);
  if (pingAt == null) {
    return "stale";
  }
  return args.now - pingAt <= RIDER_FRESHNESS_THRESHOLD_MS ? "live" : "stale";
}

function resolveFinalState(state: string | null | undefined): DeliveryFinalStateCode | null {
  if (state === "delivered" || state === "cancelled" || state === "failed") {
    return state;
  }
  return null;
}

function resolveActionCode(args: {
  finalState: DeliveryFinalStateCode | null;
  session: SessionState;
}): DeliveryTrackingActionCode | null {
  if (!args.finalState) {
    return null;
  }
  // Cancelled and failed are always `backToOrder`; the only auth-sensitive
  // CTA is the delivered case, which routes authenticated customers toward
  // the rating flow.
  if (args.finalState !== "delivered") {
    return { kind: "backToOrder" };
  }
  if (args.session === "authenticated") {
    return { kind: "rateOrder" };
  }
  // Guest or unresolved session: default conservatively to back-to-order so
  // we never expose a rating CTA that the API cannot satisfy.
  return { kind: "backToOrder" };
}

function resolveNoticeCode(args: {
  track: DeliveryTrackLike;
  finalState: DeliveryFinalStateCode | null;
  markers: DeliveryMarker[];
  riderFreshness: RiderMarkerFreshness | null;
  isEmpty: boolean;
}): DeliveryTrackingNoticeCode | null {
  // The final-state overlay carries its own copy, so we suppress the notice
  // surface whenever a final state is active.
  if (args.finalState) {
    return null;
  }
  if (args.track == null) {
    return "loading";
  }
  if (args.isEmpty) {
    return "missingOrderPoints";
  }
  const destination = args.markers.find((m) => m.kind === "destination");
  const hasOtherPoints = args.markers.some((m) => m.kind !== "destination");
  if (!destination && hasOtherPoints) {
    return "missingDestination";
  }
  const state = args.track.state ?? null;
  if (state === "unassigned") {
    return "preparing";
  }
  if (state === "assigned") {
    return "waitingAcceptance";
  }
  if (state && RIDER_ACTIVE_STATES.has(state) && args.riderFreshness == null) {
    return "waitingRiderLocation";
  }
  if (args.riderFreshness === "stale") {
    return "staleRiderLocation";
  }
  return null;
}

export type BuildDeliveryTrackingMapStateOptions = {
  /** Auth session state used to choose the post-tracking CTA. Defaults to guest. */
  session?: SessionState;
  /** Override the current time for deterministic tests. */
  now?: number;
};

export function buildDeliveryTrackingMapState(
  track: DeliveryTrackLike,
  options: BuildDeliveryTrackingMapStateOptions = {},
): DeliveryTrackingMapState {
  const now = options.now ?? Date.now();
  const session: SessionState = options.session ?? "guest";

  const origin = buildMarker("origin", track?.originLat, track?.originLng);
  const destination = buildMarker("destination", track?.destinationLat, track?.destinationLng);

  // Apply the same Rider-location visibility rule as the backend so the
  // frontend never displays a Rider marker from a payload that should not
  // carry coordinates (e.g. cached pre-accept state).
  const eligible = isRiderLocationEligible(track?.state, track?.acceptedAt);
  const riderMarker =
    eligible && track?.lastLat != null && track?.lastLng != null
      ? buildMarker("rider", track.lastLat, track.lastLng)
      : null;

  const markers: DeliveryMarker[] = [];
  if (origin) {
    markers.push(origin);
  }
  if (destination) {
    markers.push(destination);
  }

  let riderFreshness: RiderMarkerFreshness | null = null;
  if (riderMarker) {
    riderFreshness = classifyRiderFreshness({
      state: track?.state ?? null,
      lastPingAt: track?.lastPingAt,
      now,
    });
    markers.push({ ...riderMarker, freshness: riderFreshness });
  }

  const isEmpty = markers.length === 0;
  const finalState = resolveFinalState(track?.state ?? null);

  return {
    markers,
    isEmpty,
    notice: resolveNoticeCode({
      track,
      finalState,
      markers,
      riderFreshness,
      isEmpty,
    }),
    finalState,
    action: resolveActionCode({ finalState, session }),
  };
}

function buildMarker(
  kind: DeliveryMarkerKind,
  rawLat: string | number | null | undefined,
  rawLng: string | number | null | undefined,
): DeliveryMarker | null {
  const lat = parseTrackingCoordinate(rawLat);
  const lng = parseTrackingCoordinate(rawLng);
  if (lat === null || lng === null) {
    return null;
  }
  return { kind, lat, lng };
}
